<script>
import { mapActions, mapGetters } from 'vuex'
import { ReasonsForAbsence } from '@/shared/constants/booking/ReasonsForAbsence'
import getSelectedDayText from '@/helpers/get-replace-me-selected-day-text'

export default {
  name: 'ReplaceMeForm',
  props: {
    payload: {
      type: Object,
      required: true,
    },
  },
  data: () => {
    return {
      isValid: true,
      rules: {
        required: () => false,
      },
    }
  },
  computed: {
    ...mapGetters({
      getSelectedClients: 'client/getSelectedClients',
      getAllClientLocationsWithReplaceMePermission:
        'auth/getAllClientLocationsWithReplaceMePermission',
      isLoadingGrades: 'client/isLoadingGrades',
      grades: 'client/grades',
      isLoading: 'bookings/isSubmitReplaceMeLoading',
    }),
    locations() {
      return this.getAllClientLocationsWithReplaceMePermission(
        this.getSelectedClients[0] // TODO: replace with selected client id data prop
      )
    },
    selectedLocationsName() {
      const location = this.locations.find(
        (x) => x.locationId === this.payload.locationId
      )

      return !location ? '' : location?.locationName
    },
    reasons() {
      return ReasonsForAbsence
    },
    gradesList() {
      return this.grades(this.getSelectedClients[0]) // TODO: replace with selected client id data prop
    },
    isNextDayMonday() {
      return this.payload.isNextMonday
    },
    compSelectedDay() {
      return getSelectedDayText(this.payload.selectedDay, this.isNextDayMonday)
    },
  },
  created() {
    this.initValidationRules()
  },
  methods: {
    ...mapActions({
      submitReplaceMeBooking: 'bookings/submitReplaceMeBooking',
    }),
    initValidationRules() {
      this.rules = {
        required: (v) => !!v || this.$t('input.inputRequiredValidationText'),
        requiredWithAtleastOneItemSelected: (v) =>
          !!(v && v.length) || this.$t('input.inputRequiredValidationText'),
      }
    },
    close(refreshData) {
      this.$emit('closeModal', refreshData)
    },
    validate() {
      return this.$refs.replaceMeForm.validate()
    },
    async submit() {
      this.validate()
      if (!this.isValid) return

      const result = await this.submitReplaceMeBooking(this.payload)

      if (!result.isSuccess) return

      // Close form and refresh replace me status check data
      this.close(true)
    },
  },
}
</script>

<template>
  <v-card>
    <v-card-title>{{ $t('replaceMe.formTitle') }}</v-card-title>
    <v-card-text>
      <v-form
        ref="replaceMeForm"
        v-model="isValid"
        lazy-validation
        autocomplete="off"
      >
        <p class="mb-0">
          {{
            $t('replaceMe.formLocationSelectQuestionText', {
              selectedDay: compSelectedDay,
            })
          }}
        </p>
        <v-select
          v-if="locations.length > 1"
          id="replace-me-form__location-select"
          ref="replace-me-form__location-select"
          v-model="payload.locationId"
          :label="$t('bookingCreate.formLocationTitle')"
          :items="locations"
          item-text="locationName"
          item-value="locationId"
          :hint="$t('replaceMe.formLocationSelectInputHint')"
          persistent-hint
          :rules="[rules.required]"
        ></v-select>
        <v-text-field
          v-else
          id="replace-me-form__location-select"
          ref="replace-me-form__location-select"
          :label="$t('bookingCreate.formLocationTitle')"
          readonly
          :value="selectedLocationsName"
        ></v-text-field>

        <p class="mb-0 mt-6-">
          {{
            $t('replaceMe.formReasonSelectQuestionText', {
              selectedDay: compSelectedDay,
            })
          }}
        </p>

        <v-select
          id="replace-me-form__reason-select"
          ref="replace-me-form__reason-select"
          v-model="payload.reasonForAbsence"
          :label="`${$t('bookingCreate.formReasonInputLabel')} *`"
          :items="reasons"
          item-text="name"
          item-value="id"
          :rules="[rules.required]"
        ></v-select>

        <p class="mb-0 mt-4">
          {{ $t('replaceMe.formDutiesQuestionText') }}
        </p>

        <v-autocomplete
          id="replace-me-form__grade-select"
          ref="replace-me-form__grade-select"
          v-model="payload.subjectList"
          :label="`${$t('replaceMe.formSelectGradesInputLabel')} *`"
          :items="gradesList"
          item-text="title"
          item-value="id"
          multiple
          :loading="isLoadingGrades"
          chips
          small-chips
          :rules="[rules.requiredWithAtleastOneItemSelected]"
        ></v-autocomplete>

        <v-text-field
          id="replace-me-form__room-input"
          v-model="payload.room"
          :label="`${$t('bookingCreate.formRoomInputLabel')} *`"
          :rules="[rules.required]"
        />

        <p class="mb-0 mt-4">
          {{ $t('replaceMe.formNotesQuestionText') }}
        </p>

        <v-textarea
          id="replace-me-form__notes-input"
          ref="replace-me-form__notes-input"
          v-model="payload.additionalNotes"
          name="notes"
          :label="$t('bookingCreate.formNotesInputLabel')"
          auto-grow
          rows="1"
        ></v-textarea>
      </v-form>
    </v-card-text>
    <v-divider></v-divider>
    <v-card-actions>
      <v-spacer />
      <v-btn
        id="replace-me-form__cancel-btn"
        large
        text
        :disabled="isLoading"
        @click="close(false)"
      >
        {{ $t('general.cancel') }}
      </v-btn>
      <v-btn
        id="replace-me-form__submit-btn"
        :disabled="!isValid"
        :loading="isLoading"
        large
        color="blue"
        text
        @click="submit"
      >
        {{ $t('general.submit') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
